import { type FC, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Calendar } from '../calendar';

import { DateInput } from './date-input';
import { PRESETS } from './presets';
import type { DateRangePickerProps } from './types';
import { getPresetRange, normalizeToEndOfDay, normalizeToStartOfDay } from './utils';

interface ContentProps extends DateRangePickerProps {
  activePreset?: string | null;
  setActivePreset?: (preset: string | null) => void;
  /**
   * Footer Today. Off unless a host opts in — no layout of this picker turns it
   * on for you.
   */
  showTodayButton?: boolean;
}

export const DateRangePickerContent: FC<ContentProps> = ({
  range,
  setRange,
  rangeCompare,
  setRangeCompare,
  hideCustomDates,
  monthsLayout = 'column',
  activePreset,
  setActivePreset,
  showTodayButton,
  onReset,
  disabledDates,
}) => {
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' && window.innerWidth < 960
  );

  // Track window size for responsive month count
  const handleResize = useDebouncedCallback(() => {
    setIsSmallScreen(window.innerWidth < 960);
  }, 250);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Keep a host-owned preset atom in sync when the calendar range matches one
  useEffect(() => {
    if (!setActivePreset) return;
    const match = PRESETS.find((p) => {
      const { from: pf, to: pt } = getPresetRange(p.name);
      const normalize = (d?: Date) => (d ? new Date(d).setHours(0, 0, 0, 0) : null);
      return (
        normalize(range.from) === normalize(pf) && normalize(range.to) === normalize(pt)
      );
    });
    if (match?.name !== activePreset) {
      setActivePreset(match?.name || null);
    }
  }, [range, setActivePreset, activePreset]);

  return (
    <div className="flex">
      <div className="flex flex-col">
        {!hideCustomDates && (
          <div className="flex items-center gap-2 px-3 pb-4 lg:pb-0">
            <DateInput
              value={range.from}
              onChange={(date) =>
                setRange({ from: normalizeToStartOfDay(date), to: range.to })
              }
            />
            <span className="py-1">–</span>
            <DateInput
              value={range.to}
              onChange={(date) =>
                setRange({ from: range.from, to: normalizeToEndOfDay(date) })
              }
            />
          </div>
        )}

        {rangeCompare && setRangeCompare && !hideCustomDates && (
          <div className="flex items-center gap-2 px-3 pb-4 lg:pb-0">
            <DateInput
              value={rangeCompare.from}
              onChange={(date) =>
                setRangeCompare({
                  from: normalizeToStartOfDay(date),
                  to: rangeCompare.to,
                })
              }
            />
            <span className="py-1">–</span>
            <DateInput
              value={rangeCompare.to}
              onChange={(date) =>
                setRangeCompare({
                  from: rangeCompare.from,
                  to: normalizeToEndOfDay(date),
                })
              }
            />
          </div>
        )}

        <Calendar
          mode="range"
          showOutsideDays={false}
          showTodayButton={showTodayButton}
          onReset={onReset}
          onSelect={(value: { from?: Date; to?: Date } | undefined) => {
            if (value?.from != null) {
              // Normalize dates: from -> start of day (00:00:00), to -> end of day (23:59:59.999)
              const normalizedFrom = normalizeToStartOfDay(value.from);
              const normalizedTo = value?.to ? normalizeToEndOfDay(value.to) : undefined;
              setRange({ from: normalizedFrom, to: normalizedTo });
            }
          }}
          disabled={disabledDates}
          selected={{
            from: range.from ?? undefined,
            to: range.to ?? undefined,
          }}
          numberOfMonths={isSmallScreen ? 1 : 2}
          monthsLayout={monthsLayout}
          defaultMonth={
            new Date(new Date().setMonth(new Date().getMonth() - (isSmallScreen ? 0 : 1)))
          }
        />
      </div>
    </div>
  );
};

DateRangePickerContent.displayName = 'DateRangePickerContent';
